import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { persistedReportRecords } from '../../services/mockData/reports';
import { createRecord, updateRecord } from '../../services/recordStorageService';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { useToast } from '../../contexts/ToastContext';
import { ListViewRecord } from '../../types/listView';
import { FileText, Play, History } from 'lucide-react';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  report?: ListViewRecord | null;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  report
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!report;

  // Get page layout for this report
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Report__c', report);

  console.log('📝 Report Modal - Page Layout:', {
    reportId: report?.id,
    layoutName: pageLayout?.name,
    sectionCount: pageLayout?.sections?.length
  });

  const [formData, setFormData] = useState({
    name: '',
    category: 'Sales',
    reportType: 'Tabular',
    objectName: 'Lead',
    createdBy: 'John Smith',
    description: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form in edit mode
  useEffect(() => {
    if (report) {
      setFormData({
        name: report.name as string || '',
        category: report.category as string || 'Sales',
        reportType: report.reportType as string || 'Tabular',
        objectName: report.objectName as string || 'Lead',
        createdBy: report.createdBy as string || 'John Smith',
        description: report.description as string || ''
      });
    } else {
      // Reset form for create mode
      setFormData({
        name: '',
        category: 'Sales',
        reportType: 'Tabular',
        objectName: 'Lead',
        createdBy: 'John Smith',
        description: ''
      });
    }
    setErrors({});
  }, [report, isOpen]);

  // Handle field changes (for dynamic fields)
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle change events (legacy support)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Report name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const gobodoData: Record<string, any> = {
        Name__c: data.name,
        Category__c: data.category,
        ReportType__c: data.reportType,
        ObjectName__c: data.objectName,
        Owner__c: data.createdBy,
        Description__c: data.description || ''
      };
      Object.keys(gobodoData).forEach(key => {
        if (gobodoData[key] === null || gobodoData[key] === '' || gobodoData[key] === undefined) {
          delete gobodoData[key];
        }
      });

      if (isEditMode && report) {
        const updated = await updateRecord('Report__c', String(report.id), gobodoData);
        if (!updated) throw new Error('Failed to update report');
        return { ...report, ...data, id: updated.id };
      } else {
        const created = await createRecord('Report__c', gobodoData);
        return { ...data, id: created.id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reportListViews'] });
      queryClient.invalidateQueries({ queryKey: ['reportRecords'] });
      queryClient.invalidateQueries({ queryKey: ['reportListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Report__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Report'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['report', report?.id] });
      }

      success(
        isEditMode ? 'Report updated' : 'Report created',
        isEditMode ? `${data.name} has been updated` : `${data.name} has been created`
      );
      onClose();
    },
    onError: () => {
      showError('Error', 'Failed to save report. Please try again.');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    saveMutation.mutate(formData);
  };

  // Conditional tabs based on mode
  const tabs = isEditMode
    ? [
        { id: 'overview', label: 'Overview', icon: FileText },
        { id: 'results', label: 'Results', icon: Play },
        { id: 'history', label: 'History', icon: History }
      ]
    : [
        { id: 'overview', label: 'Overview', icon: FileText }
      ];

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Report' : 'New Report'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Report'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                <>
                  {pageLayout.sections.map(section => (
                    <EditSection
                      key={section.id}
                      section={section}
                      objectApiName="Report__c"
                      formData={formData}
                      onChange={handleFieldChange}
                      errors={errors}
                      disabled={saveMutation.isPending}
                    />
                  ))}

                  {!isEditMode && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Note:</strong> After creating the report, you'll be able to configure fields, filters, and grouping in the report builder.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Report.</p>
                  <p className="text-sm mt-2">Go to Object Manager to configure page layouts.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'results' && (
            <div className="text-center py-12">
              <Play className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Run Report to See Results
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Click "Run Report" from the detail page to execute this report and view results.
              </p>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Run History
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Run history will appear here. You'll see when the report was last executed and by whom.
              </p>
            </div>
          )}
        </form>
      )}
    </TabbedModal>
  );
};
